import { graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';

import useMembershipPricing from '@hooks/useMembershipPricing';
import usePaymentMethod from '@hooks/usePaymentMethod';

import { keysToCamel } from '@utils/butter';

import { IPageProps } from '@folx-types/flow-steps';

import ReactivateMembershipSummary, {
	IReactivateMembershipSummaryPageContent,
} from './ReactivateMembershipSummary.component';

interface IReactivateMembershipSummaryPageProps extends IPageProps {}

const ReactivateMembershipSummaryPage: FC<IReactivateMembershipSummaryPageProps> = ({ path }) => {
	const { pageContent, sharedMembershipContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "mssp-reactivate-membership-summary-page" }) {
				heading
				charge_date_text
				charge_frequency_text
				disclaimer
			}
			sharedMembershipContent: butterPage(slug: { eq: "shared-folx-membership-information" }) {
				folx_membership_benefits_card {
					title
					membership_topics {
						header
						info
					}
				}
				folx_membership_option_monthly {
					title
					price
					description
				}
				folx_membership_option_yearly {
					title
					price
					badge
					description
				}
			}
		}
	`);

	const { value: membershipMonthlyPricing, loading: membershipMonthlyPricingLoading } =
		useMembershipPricing('MONTHLY');

	const { value: membershipAnnualPricing, loading: membershipAnnualPricingLoading } =
		useMembershipPricing('ANNUAL');

	const refinedContent: IReactivateMembershipSummaryPageContent = keysToCamel({
		...pageContent,
		...sharedMembershipContent,
	}) as IReactivateMembershipSummaryPageContent;

	const { loading: paymentMethodLoading, value: paymentMethod } = usePaymentMethod();

	return (
		<ReactivateMembershipSummary
			pageContent={refinedContent}
			path={path}
			loading={
				paymentMethodLoading || membershipMonthlyPricingLoading || membershipAnnualPricingLoading
			}
			price={{
				annual: membershipAnnualPricing?.price ?? 0,
				monthly: membershipMonthlyPricing?.price ?? 0,
			}}
			paymentInfo={
				paymentMethod?.brand && paymentMethod.last4
					? `${paymentMethod.brand} ending in ${paymentMethod.last4}`
					: ''
			}
		/>
	);
};

export default ReactivateMembershipSummaryPage;
